import { enhanceProduct, enhanceFacets } from "../helpers/internals";
import {FacetItem, FacetProperty, ExtractedOptions, SearchResult} from 'types/index';
import { getCMSLabels } from '~/helpers/globalLabels';
import { useUserStore } from '~/stores/user-store'

interface Product {
  hasExclusivePrice?: boolean,
  coveoData?: {
    raw?: {
      onlyavailableto?: string;
      price_regular_6001?: number;
      price_discounted_6001?: number;
      price_discountpercentage_6001?: number;
      price_discountpercentage?: number;
    };
  };
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getCoveoToken = (tokenObj) => tokenObj?.[0]?.token;

// This will listen product object from the Coveo and return the title as string

export const getCoveoProductTitle = (productObj) => productObj?.name;
  

// This will listen product object from the Coveo and return the excerpt as string

export const getCoveoProductExcerpt = (productObj) => productObj?.Excerpt;

// This will listen to productObj from the Coveo and return the object of regular,discounted and the lowest price

export const getCoveoProductPrices = (productObj) => {
  const {
    regularPrice = 0,
    discountedPrice = 0,
    lowestPrice = 0,
  } = productObj || {};
  return {
    price_regular: regularPrice,
    price_discounted: discountedPrice,
    lowest_price: lowestPrice,
  };
};

// This will listen to product object from the Coveo and return the discount percentage in number

export const getCoveoPriceDiscountPercentage = ({
  regularPrice,
  specialPrice,
}) => {
  if (typeof regularPrice === "number" && typeof specialPrice === "number") {
    const discountPercentage =
      ((regularPrice - specialPrice) / regularPrice) * 100;
    return discountPercentage.toFixed(2);
  }
  return "0.00"; // Default to 0% discount if prices are not valid numbers.
};
/**
 * Calculates the difference in days between two dates.
 * @param {Date} targetDate - The target date to compare.
 * @returns {number} The difference in days.
 */
const getDifferenceInDays = (targetDate) => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const timeDifference = Date.now() - targetDate.getTime();
  return Math.floor(timeDifference / millisecondsPerDay);
};

/**
 * Gets the label for a Coveo product based on its properties.
 * @param {object} productObj - The product object to get the label for.
 * @returns {string} The product label.
 */
export const getCoveoProductLabel = (productObj) => {
  if (!productObj) return "";
  const userState = useUserStore();
  const isUserLoggedIn = userState.getUserIsLoggedIn;
  const { regularPrice, specialPrice, onlinefrom, isOnDiscount, hasExclusivePrice } = productObj;

  // Determine if the product is discounted
  const isDiscounted = isOnDiscount && specialPrice > 0 && specialPrice < regularPrice;

  // Determine the sale label
  const saleLabel = isDiscounted && (!isUserLoggedIn || !hasExclusivePrice);

  if (saleLabel) {
    return getCMSLabels("sale") || "Sale";
  }

  // Determine the freshness label
  if (onlinefrom) {
    const productFreshness = getDifferenceInDays(new Date(onlinefrom));
    if (productFreshness < 60) {
      return getCMSLabels("new") || "New";
    }
  }

  return "";
};

export const getFilteredProducts = (productObj) => enhanceProduct(productObj?.results);

export const getTotalCountProducts = (result: SearchResult): number | undefined => result?.totalCount;

export const getTotalCountFilteredProducts = (result: SearchResult): number | undefined => result?.totalCountFiltered;


// This will listen to product object from the Coveo and genarate the URL as per the pattern
// specified here: https://fiskars.atlassian.net/browse/MOO-1354
// {Domain}/{locale}/{classification_productgenerictype}/{classification_productspecifictype}/{product_Name}-{id}

export const generateCoveoProductURL = (productObj) => {
  const id = productObj?.id;
  const product_Name = productObj?.handle?.replace(/\W+/g, "-")?.toLowerCase();
  return `/p/${product_Name}-${id}`;
};

// read facets from Coveo
export const getCoveoFacets = (productObj) => enhanceFacets(productObj?.facets);
export const isOutOfStock = (status) => {
  return status?.toLowerCase() === "outofstock";
};

export const disableAddToCart = (status: string, price: number) => {
  return isOutOfStock(status) || !price;
}

export const getFacetItems = (
  facets: FacetItem[] | undefined,
  facetsProperties: FacetProperty[] | undefined
): ExtractedOptions => {
  const result: ExtractedOptions = {};

  facetsProperties?.forEach((facetProperty) => {
    const { facetId, displayName, displayFacetNumbers } = facetProperty;

    const facet = facets?.find((facet) => facet.facetId === facetId);

    if (!facet) {
      return;
    }
    const { value, moreValuesAvailable } = facet;
    const values = value || [];

    if (values.length > 0) {
      const facetKey = `${displayName} | ${facetId} | ${moreValuesAvailable}`;

      result[facetKey] = values.map(({ value, numberOfResults, state }) => ({
        facetName: displayName,
        facetId,
        label: value,
        value,
        count: displayFacetNumbers ? numberOfResults : "",
        selected: state === "selected",
      }));
    }
  });

  return result;
};

  // get only available to value from object
  export const getOnlyAvailableTo = (product: Product): string => {
    return product?.coveoData?.raw?.onlyavailableto || "";
  };
  
  // Get price of exclusive product
  export const getProductExclusivePrices = (product: Product): Object => {
    return {
      regular_price: product?.coveoData?.raw?.price_regular_6001,
      discount_price: product?.coveoData?.raw?.price_discounted_6001,
      discounted_percentage: product?.coveoData?.raw?.price_discountpercentage_6001
    }
  };
  
  // check if product is exclusive product
  export const checkProductHasExclusivePrice = (product: Product): Boolean => {
    const { hasExclusivePrice } = product;
    // Show "Family price" badge for member group 6001 if product has a discount but isn't on regular discount
    return hasExclusivePrice;
  };


const coveoGetters = {
  getCoveoToken,
  getCoveoProductTitle,
  getCoveoProductPrices,
  getCoveoProductExcerpt,
  getCoveoPriceDiscountPercentage,
  getCoveoProductLabel,
  generateCoveoProductURL,
  getFilteredProducts,
  getFacets: getCoveoFacets,
  getFacetItems,
  getTotalCountProducts,
  getTotalCountFilteredProducts,
  inStockStatus: isOutOfStock,
  disableAddToCart,
  getOnlyAvailableTo,
  getProductExclusivePrices,
  checkProductHasExclusivePrice
};

export default coveoGetters;